import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  CreatePartyEmailQuotationExchangeChannelRequest,
  GetSystemPartiesResponse,
  ManagementPartyDto,
  UpdatePartyEmailQuotationExchangeChannelRequest,
  UpdatePartyRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  HttpGetMetaData,
  InternalAPI,
} from 'services/APIs/InternalAPI/InternalAPI'

export const PartyManagementOperations = {
  GetAll: 'PartyManagementOperations-GetAll',
  Add: 'PartyManagementOperations-Add',
  Update: 'PartyManagementOperations-Update',
  Delete: 'PartyManagementOperations-Delete',
}

export const PartyQuotationExchangeOperations = {
  enable: (partyId: string) => `PartyQuotationExchange-Enable-${partyId}`,
  disable: (partyId: string) => `PartyQuotationExchange-Disable-${partyId}`,
  createEmail: (partyId: string) =>
    `PartyQuotationExchange-CreateEmail-${partyId}`,
  updateEmail: (partyId: string, channelId: string) =>
    `PartyQuotationExchange-UpdateEmail-${partyId}-${channelId}`,
  deleteEmail: (partyId: string, channelId: string) =>
    `PartyQuotationExchange-DeleteEmail-${partyId}-${channelId}`,
}

export interface IPartyManagementAPI {
  GetAll(): Promise<ManagementPartyDto[]>
  GetAllPaged(
    meta?: HttpGetMetaData<ManagementPartyDto>
  ): Promise<GetSystemPartiesResponse>
  Add(partyDto: ManagementPartyDto): Promise<ManagementPartyDto>
  Update(partyDto: ManagementPartyDto): Promise<ManagementPartyDto>
  Delete(partyId: string): Promise<void>
  EnableQuotationExchange(partyId: string): Promise<ManagementPartyDto>
  DisableQuotationExchange(partyId: string): Promise<ManagementPartyDto>
  CreateEmailQuotationExchangeChannel(
    partyId: string,
    request: CreatePartyEmailQuotationExchangeChannelRequest,
  ): Promise<ManagementPartyDto>
  UpdateEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
    request: UpdatePartyEmailQuotationExchangeChannelRequest,
  ): Promise<ManagementPartyDto>
  DeleteEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
  ): Promise<ManagementPartyDto>
}

export class PartyManagementController extends BaseController<IPartyManagementAPI> {
  constructor() {
    super((onRequestChanged) => new PartyManagementAPI(onRequestChanged))
  }

  public async GetAll() {
    try {
      return await this.api.GetAll()
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetAllPaged(meta?: HttpGetMetaData<ManagementPartyDto>) {
    try {
      return await this.api.GetAllPaged(meta)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async Add(partyDto: ManagementPartyDto) {
    try {
      return await this.api.Add(partyDto)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async Update(partyDto: ManagementPartyDto) {
    try {
      return await this.api.Update(partyDto)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async Delete(partyId: string) {
    try {
      return await this.api.Delete(partyId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async EnableQuotationExchange(partyId: string) {
    try {
      return await this.api.EnableQuotationExchange(partyId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async DisableQuotationExchange(partyId: string) {
    try {
      return await this.api.DisableQuotationExchange(partyId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async CreateEmailQuotationExchangeChannel(
    partyId: string,
    request: CreatePartyEmailQuotationExchangeChannelRequest,
  ) {
    try {
      return await this.api.CreateEmailQuotationExchangeChannel(partyId, request)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UpdateEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
    request: UpdatePartyEmailQuotationExchangeChannelRequest,
  ) {
    try {
      return await this.api.UpdateEmailQuotationExchangeChannel(
        partyId,
        channelId,
        request,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async DeleteEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
  ) {
    try {
      return await this.api.DeleteEmailQuotationExchangeChannel(
        partyId,
        channelId,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}

class PartyManagementAPI extends InternalAPI implements IPartyManagementAPI {
  constructor(private onRequestChange?: OnRequestChangeCallback) {
    super('/api/system/partymanagement', onRequestChange)
  }

  public GetAll() {
    return this.GetAsync<ManagementPartyDto[]>({
      id: PartyManagementOperations.GetAll,
      relativePath: '/GetParties',
      onRequestChange: this.onRequestChange,
    })
  }

  public GetAllPaged(meta?: HttpGetMetaData<ManagementPartyDto>) {
    return this.GetPagedAsync<GetSystemPartiesResponse>({
      id: PartyManagementOperations.GetAll,
      relativePath: '/GetParties',
      _meta: meta,
    })
  }

  public Add(partyDto: ManagementPartyDto) {
    return this.PostAsync<ManagementPartyDto>({
      id: PartyManagementOperations.Add,
      relativePath: '',
      data: partyDto,
      onRequestChange: this.onRequestChange,
    })
  }

  public Update(partyDto: ManagementPartyDto) {
    const data: UpdatePartyRequest = {
      partyId: partyDto.id,
      ...partyDto,
    }

    return this.PutAsync<ManagementPartyDto>({
      id: PartyManagementOperations.Update,
      relativePath: '',
      data,
      onRequestChange: this.onRequestChange,
    })
  }

  public Delete(partyId: string) {
    return this.DeleteAsync({
      id: PartyManagementOperations.Delete,
      relativePath: '',
      data: { partyId },
      onRequestChange: this.onRequestChange,
    })
  }

  public EnableQuotationExchange(partyId: string) {
    return this.PostAsync<ManagementPartyDto>({
      id: PartyQuotationExchangeOperations.enable(partyId),
      relativePath: `/${partyId}/quotation-exchange/enable`,
      onRequestChange: this.onRequestChange,
    })
  }

  public DisableQuotationExchange(partyId: string) {
    return this.DeleteAsyncWithResp<ManagementPartyDto>({
      id: PartyQuotationExchangeOperations.disable(partyId),
      relativePath: `/${partyId}/quotation-exchange/disable`,
      onRequestChange: this.onRequestChange,
    })
  }

  public CreateEmailQuotationExchangeChannel(
    partyId: string,
    request: CreatePartyEmailQuotationExchangeChannelRequest,
  ) {
    return this.PostAsync<ManagementPartyDto>({
      id: PartyQuotationExchangeOperations.createEmail(partyId),
      relativePath: `/${partyId}/quotation-exchange-channels/email`,
      data: request,
      onRequestChange: this.onRequestChange,
    })
  }

  public UpdateEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
    request: UpdatePartyEmailQuotationExchangeChannelRequest,
  ) {
    return this.PutAsync<ManagementPartyDto>({
      id: PartyQuotationExchangeOperations.updateEmail(partyId, channelId),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}/email`,
      data: request,
      onRequestChange: this.onRequestChange,
    })
  }

  public DeleteEmailQuotationExchangeChannel(
    partyId: string,
    channelId: string,
  ) {
    return this.DeleteAsyncWithResp<ManagementPartyDto>({
      id: PartyQuotationExchangeOperations.deleteEmail(partyId, channelId),
      relativePath: `/${partyId}/quotation-exchange-channels/${channelId}`,
      onRequestChange: this.onRequestChange,
    })
  }
}
